import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import {Context} from '../contexts/WordList';
import {conversationChat} from '../helpers/externalApi';
import {useLoadWords} from '../effects/useLoadWords';
import {useAiAvailable} from '../contexts/AiAvailability';

let nextSessionId = 1;

export const ChatPractice = () => {
  const [state, dispatch] = useContext(Context);
  const {items, language} = state;
  const {aiAvailable, aiChecking} = useAiAvailable();

  // Ensure words are loaded (same pattern as WordList / StudyMode)
  useEffect(() => {
    dispatch({type: 'HAS_MORE_ITEMS', payload: true});
  }, [dispatch]);

  useLoadWords();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(
    () => `practice-${nextSessionId++}`,
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [messages]);

  // Build a system-level context prompt from the word list
  const buildContextPrompt = useCallback(
    (userMsg) => {
      const langName =
        language === 'hebrew' ? 'Hebrew' : 'Farsi';
      const sampleWords = items
        .slice(0, 40)
        .map(
          (w) =>
            `${w.wordEn} = ${w.wordFa}` +
            (w.transliteration
              ? ` (${w.transliteration})`
              : ''),
        )
        .join('; ');

      return (
        `You are a friendly language tutor helping a ` +
        `student practice ${langName} vocabulary. ` +
        `Here are some of the student's vocabulary words: ` +
        `${sampleWords}. ` +
        `Use these words in your conversation. ` +
        `Ask questions, give example sentences, ` +
        `correct mistakes, and encourage the student. ` +
        `Keep responses concise (2-4 sentences). ` +
        `Student says: "${userMsg}"`
      );
    },
    [items, language],
  );

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMessage = {role: 'user', text: trimmed};
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const prompt = buildContextPrompt(trimmed);
      const reply = await conversationChat({
        prompt,
        sessionId,
      });
      setMessages((prev) => [
        ...prev,
        {role: 'assistant', text: reply},
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: `Sorry, I couldn't respond. (${err.message})`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, buildContextPrompt]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRestart = () => {
    setMessages([]);
  };

  const langName =
    language === 'hebrew' ? 'Hebrew' : 'Farsi';

  return (
    <div className="chat-container">
      {!aiChecking && !aiAvailable && (
        <div className="chat-offline-banner">
          <span role="img" aria-label="offline">⚠️</span>
          AI service is offline — make sure you&apos;re on your home network and the ollamaclient server is running.
        </div>
      )}
      <div className="chat-header">
        <h2 className="chat-header-title">
          Practice {langName}
        </h2>
        <p className="chat-header-subtitle">
          Chat with an AI tutor using your vocabulary
          words
        </p>
        {messages.length > 0 && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleRestart}
          >
            <span role="img" aria-label="restart">🔄</span> Restart
          </button>
        )}
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">
            <div className="chat-empty-icon"><span role="img" aria-label="chat">💬</span></div>
            <h3>Start Practicing!</h3>
            <p>
              Say hello or ask about a word to begin
              your conversation practice.
            </p>
            <div className="chat-suggestions">
              <button
                type="button"
                className="chat-chip"
                onClick={() => {
                  setInput(
                    `Help me practice ${langName} vocabulary`,
                  );
                }}
              >
                Help me practice
              </button>
              <button
                type="button"
                className="chat-chip"
                onClick={() => {
                  setInput('Quiz me on my words');
                }}
              >
                Quiz me
              </button>
              <button
                type="button"
                className="chat-chip"
                onClick={() => {
                  setInput(
                    'Give me a sentence to translate',
                  );
                }}
              >
                Translate a sentence
              </button>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`chat-bubble chat-bubble-${msg.role}`}
          >
            <div className="chat-bubble-label">
              {msg.role === 'user' ? 'You' : 'Tutor'}
            </div>
            <div className="chat-bubble-text">
              {msg.text}
            </div>
          </div>
        ))}

        {loading && (
          <div className="chat-bubble chat-bubble-assistant">
            <div className="chat-bubble-label">Tutor</div>
            <div className="chat-bubble-text chat-typing">
              Thinking…
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="chat-input-bar">
        <input
          className="field chat-input"
          placeholder={aiAvailable ? `Type in English or ${langName}…` : 'AI service offline'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || !aiAvailable}
        />
        <button
          type="button"
          className="btn btn-primary chat-send-btn"
          onClick={handleSend}
          disabled={loading || !input.trim() || !aiAvailable}
        >
          {loading ? '⏳' : '➤'}
        </button>
      </div>
    </div>
  );
};
